import { useState, useEffect } from "react";
import { Modal, Button, Input } from "@repo/core-ui";
import { MapPin, User, Building, Store } from "lucide-react";

interface HotelFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Current values from parent
  currentCity: string;
  currentState: string;
  currentStatus: string;
  currentSalesmanCode: string;
  // Handler to apply all changes at once
  onApply: (filters: {
    city: string;
    state: string;
    status: string;
    salesmanCode: string;
  }) => void;
  onClear: () => void;
}

export const HotelFilterModal = ({
  isOpen,
  onClose,
  currentCity,
  currentState,
  currentStatus,
  currentSalesmanCode,
  onApply,
}: HotelFilterModalProps) => {
  // Local state for deferred updates
  const [city, setCity] = useState(currentCity);
  const [state, setState] = useState(currentState);
  const [status, setStatus] = useState(currentStatus);
  const [salesmanCode, setSalesmanCode] = useState(currentSalesmanCode);

  // Sync local state with props when modal opens or props change
  useEffect(() => {
    if (isOpen) {
      setCity(currentCity);
      setState(currentState);
      setStatus(currentStatus);
      setSalesmanCode(currentSalesmanCode);
    }
  }, [isOpen, currentCity, currentState, currentStatus, currentSalesmanCode]);

  const handleApply = () => {
    onApply({ city, state, status, salesmanCode });
    onClose();
  };

  const handleClearLocal = () => {
    setCity("");
    setState("");
    setStatus("all");
    setSalesmanCode("");
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Advanced Filters"
      maxWidth="max-w-lg"
      footer={
        <div className="flex justify-between w-full gap-3">
          <Button
            variant="ghost"
            onClick={handleClearLocal}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            Reset Form
          </Button>
          <Button
            onClick={handleApply}
            className="bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 border-none px-6 rounded-xl shadow-lg shadow-blue-500/20"
          >
            Apply Filters
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 px-1">
        {/* City */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2">
            <Building size={16} className="text-blue-500 dark:text-blue-400" />
            City
          </label>
          <Input
            placeholder="e.g. Varanasi"
            value={city}
            onChange={(e: any) => setCity(e.target.value)}
            className="pl-4 h-11 rounded-xl bg-white dark:bg-[#1b254b]/50 border-gray-200 dark:border-white/10 text-slate-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:ring-blue-500/20"
          />
        </div>

        {/* State */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2">
            <MapPin size={16} className="text-blue-500 dark:text-blue-400" />
            State
          </label>
          <Input
            placeholder="e.g. Uttar Pradesh"
            value={state}
            onChange={(e: any) => setState(e.target.value)}
            className="pl-4 h-11 rounded-xl bg-white dark:bg-[#1b254b]/50 border-gray-200 dark:border-white/10 text-slate-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:ring-blue-500/20"
          />
        </div>

        {/* Salesman Code */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2">
            <User size={16} className="text-blue-500 dark:text-blue-400" />
            Salesman Code
          </label>
          <Input
            placeholder="e.g. S-12345"
            value={salesmanCode}
            onChange={(e: any) => setSalesmanCode(e.target.value)}
            className="pl-4 h-11 rounded-xl bg-white dark:bg-[#1b254b]/50 border-gray-200 dark:border-white/10 text-slate-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:ring-blue-500/20"
          />
        </div>

        {/* Status */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2">
            <Store size={16} className="text-blue-500 dark:text-blue-400" />
            Status
          </label>

          <div className="relative">
            <select
              className="w-full h-11 pl-4 pr-10 text-sm bg-gray-50 dark:bg-[#1b254b]/50 border border-gray-200 dark:border-white/10 text-slate-800 dark:text-slate-100 rounded-xl focus:outline-none focus:bg-white dark:focus:bg-[#0b1437] focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all appearance-none cursor-pointer"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option
                value="all"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                All Status
              </option>
              <option
                value="active"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                Active Only
              </option>
              <option
                value="inactive"
                className="dark:bg-[#111c44] dark:text-slate-200"
              >
                Inactive Only
              </option>
            </select>

            {/* Arrow */}
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400 dark:text-slate-500">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
